import { FieldConfig } from '../ModalFieldRenderer';
import { Manufacturer } from '../../../services/api/domains/manufacturers/types';
import { validationHelpers } from '../utils/validation';
import { formatDateTime } from 'utils/dateConversion';
import { FacilitySummary } from 'services/api/domains/products/types';

export type ManufacturerFieldOptions = {
  facilities?: FacilitySummary[];
};

export const getManufacturerFields = (
  opts: ManufacturerFieldOptions = {}
): FieldConfig<Manufacturer>[] => [
  // Basic text fields
  {
    key: 'manufacturer_name',
    label: 'Manufacturer Name',
    type: 'text',
    getValue: m => m.manufacturer_name || '',
    setValue: (m, val) => ({ ...m, manufacturer_name: val }),
    validator: validationHelpers.combine(
      validationHelpers.required,
      validationHelpers.minLength(2)
    ),
    isRequired: true,
  },
  {
    key: 'facilities',
    label: 'Facilities',
    type: 'multiselect',
    getValue: m =>
      Array.isArray(m.facilities)
        ? m.facilities.map(f => f.facility_id?.toString() || '')
        : [],
    setValue: (m, val) => ({
      ...m,
      facilities: Array.isArray(val)
        ? val.map(id => {
            const facility = opts.facilities?.find(
              f => f.facility_id?.toString() === id
            );
            return facility
              ? {
                  facility_id: facility.facility_id,
                  facility_name: facility.facility_name,
                }
              : { facility_id: parseInt(id, 10), facility_name: '' };
          })
        : [],
    }),
    options:
      opts.facilities?.map(f => ({
        value: f.facility_id?.toString() || '',
        label: f.facility_name || '',
      })) || [],
    isRequired: false,
  },
  {
    key: 'created_date',
    label: 'Created',
    type: 'readonly',
    getValue: f => f.created_date || '',
    formatter: formatDateTime,
    isAlwaysDisabled: true,
  },

  {
    key: 'modified_date',
    label: 'Last Updated',
    type: 'readonly',
    getValue: f => f.modified_date || '',
    formatter: formatDateTime,
    isAlwaysDisabled: true,
  },

  {
    key: 'user_id',
    label: 'User ID',
    type: 'readonly',
    getValue: f => f.user_id || '',
    isAlwaysDisabled: true,
  },
];

// ID field for display in view mode
export const manufacturerIdField: FieldConfig<Manufacturer> = {
  key: 'manufacturer_id',
  label: 'Manufacturer ID',
  type: 'text',
  getValue: m => m.manufacturer_id?.toString() || '',
  setValue: (m, val) => ({ ...m, manufacturer_id: parseInt(val) || 0 }),
  isRequired: false,
};

// Fields that should always be disabled (readonly)
export const manufacturerAlwaysDisabledFields: (keyof Manufacturer)[] = [
  'manufacturer_id',
  'created_date',
  'modified_date',
  'user_id',
];
